import React, { useState, useMemo, useEffect } from 'react';
import { isNil } from 'ramda';

import useDialog from 'lib/hooks/useDialog';

import CreatePatientDialog from './CreatePatientDialog';
import CreateSuccessDialog from './CreateSuccessDialog';
import EmptyPage from './EmptyPage';
import LoadingPage from 'components/LoadingPage';
import AddCarePlanDialog from 'components/ui/AddCarePlanDialog/index';

import {
  useAddAppointmentDialog,
  usePageLoading,
  useSetFilters,
} from './hooks';
import { PatientsPageContainer } from './styled/patientsPageLayout';
import AppointmentDialog from '../SchedulePage/AppointmentDialog';
import ImportButton from './components/buttons/ImportButton';
import PatientsListBody from './PatientsListBody';
import useGetPatients from './hooks/useGetPatients';
import useCreatePatient from './hooks/useCreatePatient';
import ImportPatientsDialog from './ImportPatientsDialog';
// import { GET_PERMISSION_LIST } from 'pagesAdmin/SettingsPage/gqlSchemes/getPermissionList';

export const TableContext = React.createContext({});

function PatientsPage() {

  const createPatientDialog = useDialog();
  const createSuccessDialog = useDialog();
  const importPatientsDialog = useDialog();
  const addCarePlanDialog = useDialog();

  const [pageNumber, setPageNumber] = useState(0);
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(paginationInfo?.perPage || 5);

  const {
    appointmentDialog,
    onOpenAppointmentDialog,
  } = useAddAppointmentDialog();

  const {
    filters,
    setFilters,
    resetFilters,
    handleSearch,
    debouncedFilters,
    hasFiltersActive,
    getActiveFilterTypes,
  } = useSetFilters();

  // Open filter panel by default if there are active filters from URL
  const [isFiltersOpen, setIsFiltersOpen] = useState(() => hasFiltersActive());

  useEffect(() => {
    setPageNumber(0);
  }, [filters]);

  const onSearch = (event) => {
    handleSearch(event.target.value);
  };

  const { patients, loading } = useGetPatients({
    filter: debouncedFilters,
    perPage: rowsPerPage,
    page: pageNumber,
    setPageNumber,
    setPaginationInfo,
  });

  const { createPatient } = useCreatePatient(
    openNewPatientDialog,
    createSuccessDialog
  );

  const pageLoading = usePageLoading(loading);

  async function openNewPatientDialog() {
    await createPatientDialog.open({ createPatient });
  }

  const valueForTableContext = useMemo(
    () => ({
      patients,
      paginationInfo,
      setPageNumber,
      pageNumber,
      rowsPerPage,
      setRowsPerPage,
      loading,
      isFiltersOpen,
      filters,
      setFilters,
      onOpenAppointmentDialog,
      onAddCarePlan: (patient) => addCarePlanDialog.open({ mode: 'patient', patient }),
    }),
    [
      patients,
      paginationInfo,
      setPageNumber,
      pageNumber,
      rowsPerPage,
      setRowsPerPage,
      isFiltersOpen,
      loading,
      filters,
      setFilters,
      onOpenAppointmentDialog,
      addCarePlanDialog,
    ]
  );

  return (
    <div>
      {isNil(patients) ? (
        loading ? (
          <LoadingPage />
        ) : (
          <EmptyPage openNewPatientDialog={openNewPatientDialog} />
        )
      ) : pageLoading ? (
        <LoadingPage />
      ) : (
        <PatientsPageContainer isOpen={isFiltersOpen}>
          <PatientsListBody
            filters={filters}
            isFiltersOpen={isFiltersOpen}
            setFilters={setFilters}
            patients={patients}
            valueForTableContext={valueForTableContext}
            resetFilters={resetFilters}
            activeFilterTypes={getActiveFilterTypes()}
            filterName={filters.name || ''}
            onFilterButtonClick={setIsFiltersOpen}
            onSearch={onSearch}
            openNewPatientDialog={openNewPatientDialog}
            openImportPaitentDialog={importPatientsDialog.open}
          />
        </PatientsPageContainer>
      )}
      {createPatientDialog.isOpen && (
        <CreatePatientDialog
          isOpen={createPatientDialog.isOpen}
          close={createPatientDialog.close}
          initialData={createPatientDialog.initialData}
        />
      )}
      {addCarePlanDialog.isOpen && (
        <AddCarePlanDialog 
          isOpen={addCarePlanDialog.isOpen}
          close={addCarePlanDialog.close}
          mode={addCarePlanDialog.initialData?.mode}
          patient={addCarePlanDialog.initialData?.patient}
          patientGroup={addCarePlanDialog.initialData?.patientGroup}
        />
      )}
      {appointmentDialog.isOpen && (
        <AppointmentDialog
          isOpen={appointmentDialog.isOpen}
          close={appointmentDialog.close}
          initialData={appointmentDialog.initialData}
        />
      )}
      {createSuccessDialog.isOpen && (
        <CreateSuccessDialog
          isOpen={createSuccessDialog.isOpen}
          close={createSuccessDialog.close}
          initialData={createSuccessDialog.initialData}
        />
      )}
      {importPatientsDialog.isOpen && (
        <ImportPatientsDialog
          isOpen={importPatientsDialog.isOpen}
          close={importPatientsDialog.close}
          initialData={importPatientsDialog.initialData}
        />
      )}
    </div>
  );
}

export default PatientsPage;
